import axios from "axios";
import base_url from "../../api/bootapi";

export const AIRLINE_RULES = {
  LH: {
    names: ["lufthansa"],
    codes: ["LH", "DLH"],
  },
  VL: {
    names: ["lufthansa city airlines"],
    codes: ["VL", "LHX"],
  },
};

export const LUFTHANSA_TERMS_URL =
  "https://api.linsoatours.com/Images/other_document/_FLT__Airline_claims_-_T_C_-_GERMANY_311f533b.pdf";
export const DEFAULT_TERMS_URL =
  "https://api.linsoatours.com/Images/other_document/FLT_Airline_claims_-_TC_1.pdf";

export const getTermsAndConditionsUrl = (isLufthansa) =>
  isLufthansa ? LUFTHANSA_TERMS_URL : DEFAULT_TERMS_URL;

export const normalizeAirlineName = (rawName) => {
  if (!rawName || typeof rawName !== "string") return "";
  return rawName
    .toLowerCase()
    .replace(/\s*\([a-z0-9]+\)\s*$/i, "") // strips trailing (LH), (VL), etc.
    .replace(/\s+/g, " ")
    .trim();
};

export const isLufthansaFlight = (flight) => {
  if (!flight) return false;

  const candidateNames = [];
  const candidateCodes = [];

  if (typeof flight === "string") {
    const trimmed = flight.trim();
    if (trimmed.length <= 3) {
      candidateCodes.push(trimmed.toUpperCase());
    } else {
      candidateNames.push(trimmed);
    }
  } else if (typeof flight === "object") {
    if (typeof flight.airline === "string") candidateNames.push(flight.airline);
    if (flight.airline?.airline_name) candidateNames.push(flight.airline.airline_name);
    if (flight.airline?.label) candidateNames.push(flight.airline.label);
    if (flight.airline_name) candidateNames.push(flight.airline_name);
    if (flight.callsign) candidateNames.push(flight.callsign);
    if (typeof flight.actual_airline === "string") candidateNames.push(flight.actual_airline);
    if (flight.actual_airline?.airline_name) candidateNames.push(flight.actual_airline.airline_name);
    if (flight.actual_airline?.label) candidateNames.push(flight.actual_airline.label);

    const rawCodes = [
      flight.hub_code,
      flight.airline_code,
      flight.iata_code,
      flight.icao_code,
      flight.actual_hub_code,
      flight.airline?.iata_code,
      flight.airline?.hub_code,
      flight.airline?.icao_code,
      flight.actual_airline?.iata_code,
      flight.actual_airline?.hub_code,
      flight.actual_airline?.icao_code,
    ];

    rawCodes.forEach((code) => {
      if (typeof code === "string" && code.trim()) {
        candidateCodes.push(code.trim().toUpperCase());
      }
    });

    candidateNames.forEach((name) => {
      const match = String(name).match(/\(([a-zA-Z0-9]{2,3})\)/);
      if (match) {
        candidateCodes.push(match[1].toUpperCase());
      }
    });
  }

  const cleanNames = candidateNames.flatMap((rawName) => {
    if (!rawName || typeof rawName !== "string") return [];
    const trimmed = rawName.trim().toLowerCase();
    const stripped = normalizeAirlineName(rawName);
    return [trimmed, stripped];
  });

  return Object.values(AIRLINE_RULES).some((rule) => {
    const codeMatch = candidateCodes.some((code) =>
      rule.codes.some((ruleCode) => ruleCode.toUpperCase() === code)
    );
    if (codeMatch) return true;

    const nameMatch = cleanNames.some((name) =>
      rule.names.some((ruleName) => ruleName.toLowerCase() === name)
    );
    return nameMatch;
  });
};

export const base64ToBlob = (base64, contentType = "application/pdf") => {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type: contentType });
};

export const isBaggageOrLostWagesClaim = (typeOfClaim) => {
  if (!typeOfClaim || typeof typeOfClaim !== "string") return false;
  const lower = typeOfClaim.toLowerCase().trim();
  return (
    lower === "lost/damaged/delayed baggage" ||
    lower === "lost/damaged" ||
    lower === "lost wages" ||
    lower === "lost_wages" ||
    lower === "lost_baggage" ||
    lower === "damage_baggage" ||
    lower === "delayed_baggage" ||
    lower.includes("baggage") ||
    lower.includes("wages") ||
    lower.includes("luggage")
  );
};

export const getRelevantFlightsForLufthansaCheck = (
  typeOfClaim,
  originalFlights,
  actualFlights
) => {
  if (isBaggageOrLostWagesClaim(typeOfClaim)) {
    return Array.isArray(actualFlights)
      ? actualFlights
      : actualFlights
      ? [actualFlights]
      : [];
  }
  return Array.isArray(originalFlights)
    ? originalFlights
    : originalFlights
    ? [originalFlights]
    : [];
};

export const checkIsLufthansaByClaimType = ({
  typeOfClaim,
  originalFlights,
  actualFlights,
}) => {
  const relevantFlights = getRelevantFlightsForLufthansaCheck(
    typeOfClaim,
    originalFlights,
    actualFlights
  );
  return relevantFlights.some(isLufthansaFlight);
};

export const isLufthansaClaim = (originalFlights, actualFlights, typeOfClaim) => {
  if (typeOfClaim !== undefined && typeOfClaim !== null) {
    return checkIsLufthansaByClaimType({
      typeOfClaim,
      originalFlights,
      actualFlights,
    });
  }

  const origList = Array.isArray(originalFlights)
    ? originalFlights
    : originalFlights
    ? [originalFlights]
    : [];
  const actualList = Array.isArray(actualFlights)
    ? actualFlights
    : actualFlights
    ? [actualFlights]
    : [];

  return origList.some(isLufthansaFlight) || actualList.some(isLufthansaFlight);
};

export const downloadAgreementPdf = async (
  element,
  filename = "document.pdf"
) => {
  if (!element) {
    console.error("DOM element reference is missing");
    return false;
  }
  // Hide the sign button if present inside the HTML before capturing
  const signBtn = element.querySelector("#sign-button");
  const origDisplay = signBtn ? signBtn.style.display : "";
  if (signBtn) signBtn.style.display = "none";
  try {
    const htmlString = element.innerHTML;
    // 1. Call the backend PDF generation endpoint (same as admin)
    const response = await axios.post(
      `${base_url.api1}/tourapp/power_of_attorney`,
      {
        html_string: htmlString,
      }
    );
    if (response.data && response.data.status && response.data.document) {
      const base64PDF = response.data.document;
      
      // 2. Convert Base64 response to Blob
      const blob = base64ToBlob(base64PDF, "application/pdf");
      // 3. Trigger immediate browser download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      // 4. Clean up memory
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 200);
      return true;
    } else {
      throw new Error("Backend did not return document");
    }
  } catch (error) {
    console.error("Error generating PDF via backend, fallback to print:", error);
    window.print();
    return false;
  } finally {
    if (signBtn) signBtn.style.display = origDisplay;
  }
};

